/* Жижиг хэсгүүд — дизайны queue-board.jsx-ээс */
import { IconDone, IconSearch, IconTimer, IconX } from '../shared/icons';
import { STATUS_META } from '../shared/data';
import { elapsedText, enteredTimeText } from '../shared/format';
import type { QueueCar } from '../shared/types';

export function Plate({ plate, size }: { plate: string; size?: 'lg' | 'md' }) {
  const cls = size === 'lg'
    ? 'text-[20px] px-2.5 py-[5px] border-2'
    : 'text-[15px] px-2 py-[3px] border-[1.5px]';
  return (
    <div className={'inline-flex items-center border-slate-800 bg-white text-slate-900 font-extrabold tracking-wider leading-none rounded-[7px] whitespace-nowrap ' + cls}>{plate}</div>
  );
}

export function QueuePosBadge({ pos }: { pos: number }) {
  return (
    <span className={
      'inline-flex items-center text-[11px] font-bold rounded-full px-2 py-[3px] whitespace-nowrap ' +
      (pos === 0 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500')
    }>
      {pos === 0 ? 'Дараагийн' : `${pos} машины дараа`}
    </span>
  );
}

export function WasherChip({ name, highlight, big }: { name: string; highlight?: boolean; big?: boolean }) {
  return (
    <span className={
      'inline-flex items-center gap-1 font-medium rounded-full pl-[3px] pr-2 py-[2px] ' +
      (big ? 'text-[13px] ' : 'text-[11px] ') +
      (highlight ? 'bg-cyan-50 text-cyan-800 ring-1 ring-cyan-200' : 'bg-slate-100 text-slate-600')
    }>
      <span className={
        'rounded-full font-bold text-white flex items-center justify-center ' +
        (big ? 'w-5 h-5 text-[11px] ' : 'w-4 h-4 text-[9px] ') +
        (highlight ? 'bg-[var(--brand2)]' : 'bg-slate-400')
      }>
        {name[0]}
      </span>
      {name}
    </span>
  );
}

export function ElapsedChip({ car, now, className }: { car: QueueCar; now: number; className?: string }) {
  const mins = (now - car.enteredAt) / 60000;
  const hot = mins >= 20 && car.status !== 'done';
  return (
    <span title={enteredTimeText(car.enteredAt) + ' цагт орж ирсэн'} className={
      'inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-1.5 py-[3px] whitespace-nowrap ' +
      (hot ? 'bg-amber-50 text-amber-700 ' : 'bg-slate-100 text-slate-500 ') +
      (className || '')
    }>
      <IconTimer size={12} strokeWidth={2.5} />
      {elapsedText(car.enteredAt, now)}
    </span>
  );
}

export function ActionButton({ car, onAdvance, big }: { car: QueueCar; onAdvance: (car: QueueCar) => void; big?: boolean }) {
  const meta = STATUS_META[car.status];
  const A = meta.ActionIcon;
  const isPay = car.status === 'done';
  return (
    <button
      onClick={() => onAdvance(car)}
      className={
        'w-full inline-flex items-center justify-center gap-1.5 font-semibold rounded-[calc(var(--r)-5px)] transition active:scale-[0.98] ' +
        (big ? 'min-h-[48px] text-[15px] ' : 'h-9 text-[13px] ') +
        (isPay
          ? 'text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-xs shadow-cyan-600/20 hover:opacity-95'
          : 'text-slate-700 bg-slate-100 hover:bg-slate-200')
      }>
      <A size={15} strokeWidth={2.4} />
      {meta.action}
    </button>
  );
}

export function SearchBox({ value, onChange, className }: { value: string; onChange: (v: string) => void; className?: string }) {
  return (
    <div className={'relative ' + (className || '')}>
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
        <IconSearch size={15} />
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Хайх — улсын дугаар, угаагч, үйлчилгээ…"
        className="w-full h-10 pl-8 pr-8 rounded-[var(--r)] bg-white ring-1 ring-slate-200 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] transition" />
      {value && (
        <button onClick={() => onChange('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600">
          <IconX size={14} />
        </button>
      )}
    </div>
  );
}

export function Toast({ toast }: { toast: string | null }) {
  if (!toast) return null;
  return (
    <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-slate-900 text-white text-sm font-medium rounded-full px-4 py-2.5 shadow-lg whitespace-nowrap">
      <IconDone size={17} className="text-emerald-400" />
      {toast}
    </div>
  );
}

export function CancelDialog({ car, onClose, onConfirm }: { car: QueueCar | null; onClose: () => void; onConfirm: (car: QueueCar) => void }) {
  if (!car) return null;
  return (
    <div className="absolute inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-[360px] bg-white rounded-[calc(var(--r)+4px)] shadow-2xl p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col gap-1.5">
          <div className="font-bold text-slate-900 text-[15px]">Дарааллаас цуцлах уу?</div>
          <div className="text-[13px] text-slate-500 leading-relaxed">
            <span className="font-bold text-slate-700">{car.plate}</span> · {car.type} · {car.services.join(', ')} — бичлэг дарааллаас хасагдана.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 min-h-[44px] rounded-[calc(var(--r)-4px)] text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition">
            Буцах
          </button>
          <button onClick={() => onConfirm(car)}
            className="flex-1 min-h-[44px] rounded-[calc(var(--r)-4px)] text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition">
            Цуцлах
          </button>
        </div>
      </div>
    </div>
  );
}
